import React, {useRef, useState} from 'react';
import {Input, Space, Select, Button, Modal, Typography} from 'antd';

import {PageGrid, RowAction} from '@/component';
import {getColumns} from './jobsColumns';
import {
    CaretRightOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    FileSyncOutlined,
    FileDoneOutlined,
} from '@/icons';
import {changeJobStatus, deleteJob, runJob, getJobNames} from '@/api/jobs';
import JobFormModal from '@/page/admin/JobsPage/JobFormModal';
import JobLogsModal from '@/page/admin/JobsPage/JobLogsPage/JobLogsModal';
import TaskResultModal from '@/page/admin/JobsPage/TaskResultPage/TaskResultModal';
import {useAsync} from 'react-use';

const {Option} = Select;

export default function JobsList() {
    const tableRef = useRef();
    const formModalRef = useRef();
    const logModalRef = useRef();
    const taskResultModalRef = useRef();

    const [status, setStatus] = useState(null);
    const [jobName, setJobName] = useState(null);
    const [jobGroup, setJobGroup] = useState(null);

    const {value} = useAsync(() => {
        return getJobNames();
    });

    const nameList = value?.data;

    function onSaveAfter() {
        tableRef.current.refresh();
    }

    function onDeleteClick(record) {
        const content = (
            <div>
                <p>确定删除 [{record.jobName}]？</p>
            </div>
        );

        Modal.confirm({
            title: '警告',
            content,
            okButtonProps: {danger: true},
            onOk: () => {
                deleteJob(record.jobId).then(() => {
                    refreshTable();
                });
            },
        });
    }

    function onRunClick(record) {
        const content = (
            <div>
                <p>确定要立即执行一次 [{record.jobName}] 任务吗？</p>
            </div>
        );

        Modal.confirm({
            title: '警告',
            content,
            okButtonProps: {danger: true},
            onOk: () => {
                runJob(record.jobId, record.jobGroup).then(() => {
                    refreshTable();
                });
            },
        });
    }

    function onStatusChangeClick(record) {
        return () => {
            let text = record.status === '0' ? '停用' : '启用';

            const content = (
                <div>
                    <p>
                        确认要 {text} [{record.jobName}] 任务吗？
                    </p>
                </div>
            );

            Modal.confirm({
                title: '警告',
                content,
                okButtonProps: {danger: true},
                onOk: () => {
                    record.status = record.status === '0' ? '1' : '0';
                    changeJobStatus(record.jobId, record.status).then(() => {
                        refreshTable();
                    });
                },
            });
        };
    }

    function onModifyClick(record) {
        formModalRef.current.showModal(record.jobId);
    }

    /**
     * 任务日志
     * @param record
     */
    function onJobLogView() {
        logModalRef.current.showModal();
    }

    /**
     * 执行结果
     * @param record
     */
    function onTaskResultView() {
        taskResultModalRef.current.showModal();
    }

    function onAddClick() {
        formModalRef.current.showModal();
    }

    function onStatusChange(value) {
        setStatus(value);
    }

    function onJobNameChange(value) {
        setJobName(value);
    }

    function onJobGroupChange(value) {
        setJobGroup(value);
    }

    function onSubmit() {
        const params = {
            status: status,
            jobName: jobName,
            jobGroup: jobGroup,
        };
        tableRef.current.reload2FirstPage(params);
    }

    function onReset() {
        setStatus(null);
        setJobName(null);
        setJobGroup(null);
        tableRef.current.reload2FirstPage({});
    }

    function refreshTable() {
        tableRef.current.refresh();
    }

    const columns = getColumns(
        [
            'jobId',
            'jobName',
            'jobGroup',
            'invokeTarget',
            'cronExpression',
            'status',
            'createTime',
            'updateTime',
        ],
        {onStatusChangeClick}
    );
    const actions = [
        {
            key: 'run',
            title: '执行一次',
            hide: true,
            icon: <CaretRightOutlined />,
            onClick: onRunClick,
        },
        {
            key: 'divider',
            isDivider: true,
            hide: true,
        },
        {
            key: 'modify',
            title: '修改',
            hide: true,
            icon: <EditOutlined />,
            onClick: onModifyClick,
        },
        {
            key: 'delete',
            title: '删除',
            hide: true,
            icon: <DeleteOutlined />,
            onClick: onDeleteClick,
        },
    ];
    columns.push({
        title: '操作',
        key: 'action',
        width: 100,
        render: (text, record) => <RowAction actions={actions} record={record} />,
    });

    function renderExtra() {
        return (
            <Space>
                <Select value={jobName} style={{width: 200}} onChange={onJobNameChange}>
                    <Option value={null}>任务名称</Option>
                    {nameList &&
                        nameList.map(item => (
                            <Option key={item} value={item}>
                                {item}
                            </Option>
                        ))}
                </Select>
                {/* <Select value={jobGroup} style={{width: 120}} onChange={onJobGroupChange}>
                    <Option value={null}>任务组名</Option>
                    <Option value="DEFAULT">默认</Option>
                    <Option value="SYSTEM">系统</Option>
                    <Option value="MEDIA">图文</Option>
                </Select> */}
                <Select value={status} style={{width: 120}} onChange={onStatusChange}>
                    <Option value={null}>状态</Option>
                    <Option value="0">正常</Option>
                    <Option value="1">暂停</Option>
                </Select>
                <Button type="primary" onClick={onSubmit}>
                    搜索
                </Button>
                <Button onClick={onReset}>重置</Button>
                {/*<Button type="primary" icon={<PlusOutlined />} onClick={onAddClick}>*/}
                {/*    新增*/}
                {/*</Button>*/}
                <Button icon={<FileSyncOutlined />} onClick={onJobLogView}>
                    任务日志
                </Button>
                <Button icon={<FileDoneOutlined />} onClick={onTaskResultView}>
                    执行结果
                </Button>
            </Space>
        );
    }

    return (
        <div style={{flex: 'auto', flexDirection: 'row'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
                <Typography.Title level={3}>定时任务</Typography.Title>
                <div>
                    {renderExtra()}
                </div>
            </div>
            <div style={{margin: '0 24px 24px'}}>
                <PageGrid ref={tableRef} url={'/jobs'} columns={columns} rowKey="jobId" />
            </div>
            <JobFormModal ref={formModalRef} onSaveAfter={onSaveAfter} reload={refreshTable} />
            <JobLogsModal ref={logModalRef} modalTitle="任务日志" />
            <TaskResultModal ref={taskResultModalRef} modalTitle="执行结果" />
        </div>
    );
}
